import { createSlice } from "@reduxjs/toolkit";
import {
  teamListApis,
  teamMembersApis,
  teamFlowApis,
  taskTimelineApis,
  moveTaskApis,
  upsertTeamMemberApis,
  designationsApis,
  setUserTeamsApis,
  teamWiseApis,
  upsertTeamApis,
} from "./teamActions";

/* The API answers { success, data, message }; axios wraps that in .data
   again, and one endpoint (sp_Team_GetAll) is returned bare. Unwrap once
   here so every screen reads a plain array. */
const unwrap = (payload) => {
  const body = payload?.data ?? payload;
  const inner = body?.data ?? body;
  return Array.isArray(inner) ? inner : [];
};

const initialState = {
  teams: [],
  teamsStatus: "idle",

  /* Members and flows are kept per team code, not in one list. The Assign
     grid switches teams as the user moves between rows, and a single shared
     list would let a slow response for team A overwrite team B's people. */
  designations: [],
  membersByTeam: {},
  membersStatus: "idle",

  flowByTeam: {},
  flowStatus: "idle",

  timeline: [],
  timelineStatus: "idle",

  /* Team-wise dashboard totals. Kept as { teams, grandTotal } exactly as the
     API splits them, so the panel never has to filter the GRAND TOTAL row
     back out of the per-team cards. */
  teamWise: { teams: [], grandTotal: null },
  teamWiseStatus: "idle",

  moveStatus: "idle",
  memberSaveStatus: "idle",
  error: null,
};

const teamSlice = createSlice({
  name: "team",
  initialState,
  reducers: {
    clearTimeline(state) {
      state.timeline = [];
      state.timelineStatus = "idle";
    },
    resetMoveStatus(state) {
      state.moveStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      /* One list for the whole app: the roster picker and the chart label
         must offer and show the same words. */
      .addCase(designationsApis.fulfilled, (state, action) => {
        const body = action.payload?.data ?? action.payload;
        state.designations = Array.isArray(body) ? body : body?.data ?? [];
      })
      .addCase(teamListApis.pending, (state) => {
        state.teamsStatus = "loading";
        state.error = null;
      })
      .addCase(teamListApis.fulfilled, (state, action) => {
        state.teamsStatus = "succeeded";
        state.teams = unwrap(action.payload);
      })
      .addCase(teamListApis.rejected, (state, action) => {
        state.teamsStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(teamMembersApis.pending, (state) => {
        state.membersStatus = "loading";
      })
      .addCase(teamMembersApis.fulfilled, (state, action) => {
        state.membersStatus = "succeeded";
        state.membersByTeam[action.payload.teamCode] = unwrap(
          action.payload.response,
        );
      })
      .addCase(teamMembersApis.rejected, (state, action) => {
        state.membersStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(teamFlowApis.pending, (state) => {
        state.flowStatus = "loading";
      })
      .addCase(teamFlowApis.fulfilled, (state, action) => {
        state.flowStatus = "succeeded";
        state.flowByTeam[action.payload.fromTeamCode] = unwrap(
          action.payload.response,
        );
      })
      .addCase(teamFlowApis.rejected, (state, action) => {
        state.flowStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(teamWiseApis.pending, (state) => {
        state.teamWiseStatus = "loading";
      })
      .addCase(teamWiseApis.fulfilled, (state, action) => {
        state.teamWiseStatus = "succeeded";
        const body = action.payload?.data ?? action.payload;
        const inner = body?.data ?? body;
        state.teamWise = {
          teams: Array.isArray(inner?.teams) ? inner.teams : [],
          grandTotal: inner?.grandTotal ?? null,
        };
      })
      .addCase(teamWiseApis.rejected, (state, action) => {
        state.teamWiseStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(taskTimelineApis.pending, (state) => {
        state.timelineStatus = "loading";
      })
      .addCase(taskTimelineApis.fulfilled, (state, action) => {
        state.timelineStatus = "succeeded";
        state.timeline = unwrap(action.payload);
      })
      .addCase(taskTimelineApis.rejected, (state, action) => {
        state.timelineStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(moveTaskApis.pending, (state) => {
        state.moveStatus = "loading";
      })
      .addCase(moveTaskApis.fulfilled, (state) => {
        state.moveStatus = "succeeded";
      })
      .addCase(moveTaskApis.rejected, (state, action) => {
        state.moveStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(upsertTeamMemberApis.pending, (state) => {
        state.memberSaveStatus = "loading";
      })
      .addCase(upsertTeamMemberApis.fulfilled, (state) => {
        state.memberSaveStatus = "succeeded";
      })
      .addCase(upsertTeamMemberApis.rejected, (state, action) => {
        state.memberSaveStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(setUserTeamsApis.pending, (state) => {
        state.memberSaveStatus = "loading";
      })
      .addCase(setUserTeamsApis.fulfilled, (state) => {
        state.memberSaveStatus = "succeeded";
        /* Somebody's teams changed, so every cached member list is now
           potentially wrong — an assignee dropdown built from it would still
           offer them on their old team. Dropping the cache is cheaper than
           working out which teams were touched. */
        state.membersByTeam = {};
      })
      .addCase(setUserTeamsApis.rejected, (state, action) => {
        state.memberSaveStatus = "failed";
        state.error = action.payload || action.error.message;
      })

      .addCase(upsertTeamApis.pending, (state) => {
        state.teamSaveStatus = "loading";
        state.error = null;
      })
      .addCase(upsertTeamApis.fulfilled, (state) => {
        state.teamSaveStatus = "succeeded";
        /* A new team changes who can be assigned where, so the cached member
           lists and flow routes are both stale. The page refetches the list
           itself; these two would otherwise stay wrong until a reload. */
        state.membersByTeam = {};
        state.flowByTeam = {};
      })
      .addCase(upsertTeamApis.rejected, (state, action) => {
        state.teamSaveStatus = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearTimeline, resetMoveStatus } = teamSlice.actions;
export default teamSlice.reducer;
